import React, { useState } from 'react';
import Header from '../../components/navigation/Header';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TripPlanResults from './components/TripPlanResults';
import TravelChatbot from './components/TravelChatbot';
import { generateTripPlan } from '../../services/tripPlannerService';

const TripPlanner = () => {
  const [formData, setFormData] = useState({
    location: '',
    days: '',
    budget: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tripPlan, setTripPlan] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showChatbot, setShowChatbot] = useState(false);

  const philippineDestinations = [
    { value: 'Boracay', label: 'Boracay' },
    { value: 'Palawan', label: 'Palawan (El Nido & Coron)' },
    { value: 'Siargao', label: 'Siargao' },
    { value: 'Cebu', label: 'Cebu' },
    { value: 'Bohol', label: 'Bohol' },
    { value: 'Manila', label: 'Manila' },
    { value: 'Baguio', label: 'Baguio' },
    { value: 'Vigan', label: 'Vigan' },
    { value: 'Batanes', label: 'Batanes' },
    { value: 'Camiguin', label: 'Camiguin' },
  ];

  const daysOptions = [
    { value: '3', label: '3 Days' },
    { value: '4', label: '4 Days' },
    { value: '5', label: '5 Days' },
    { value: '7', label: '7 Days' },
    { value: '10', label: '10 Days' },
    { value: '14', label: '14 Days' },
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.location) {
      newErrors.location = 'Please select a destination';
    }
    
    if (!formData?.days) {
      newErrors.days = 'Please select number of days';
    }
    
    if (!formData?.budget) {
      newErrors.budget = 'Please enter your budget';
    } else if (isNaN(formData?.budget) || Number(formData?.budget) <= 0) {
      newErrors.budget = 'Please enter a valid budget amount';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMessage('');
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const plan = await generateTripPlan({
        location: formData?.location,
        days: parseInt(formData?.days),
        budget: parseFloat(formData?.budget)
      });
      
      setTripPlan(plan);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to generate trip plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleReset = () => {
    setFormData({ location: '', days: '', budget: '' });
    setTripPlan(null);
    setErrors({});
    setErrorMessage('');
    setShowChatbot(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E8B8B]/10 via-background to-[#F4A261]/10">
      <Header />
      <div className="container mx-auto px-4 py-8 mt-20">
        {/* Hero Section with Background Image */}
        <div className="relative mb-12 rounded-3xl overflow-hidden">
          {/* Solid Gradient Background - no image */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#2E8B8B] via-[#2E8B8B]/90 to-[#F4A261]/80"></div>
          
          {/* Content */}
          <div className="relative z-10 text-center py-16 px-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Icon name="Sparkles" size={32} className="text-[#F4A261]" />
              <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                AI Smart Trip Planner
              </h1>
            </div>
            <p className="text-lg text-white/90 max-w-2xl mx-auto drop-shadow-md">
              Let AI create your perfect Philippine adventure with personalized hotel and restaurant recommendations
            </p>
            
            {/* Chatbot Toggle Button */}
            <div className="mt-6">
              <Button
                onClick={() => setShowChatbot(!showChatbot)}
                iconName="MessageCircle"
                variant="outline"
                size="lg"
                className="border-white/80 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                {showChatbot ? 'Hide Travel Assistant' : 'Chat with Travel Assistant'}
              </Button>
            </div>
          </div>
        </div>

        {/* Chatbot Section */}
        {showChatbot && (
          <div className="max-w-4xl mx-auto mb-12">
            <TravelChatbot 
              userPreferences={{
                destination: formData?.location,
                budget: formData?.budget,
                days: formData?.days
              }}
            />
          </div>
        )}

        {/* Planning Form */}
        {!tripPlan && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
              <form onSubmit={handleSubmit} className="space-y-6">
                <Select
                  label="Destination"
                  placeholder="Select your destination"
                  options={philippineDestinations}
                  value={formData?.location}
                  onChange={(value) => handleInputChange('location', value)}
                  error={errors?.location}
                  required
                  searchable
                />

                <Select
                  label="Number of Days"
                  placeholder="Select trip duration"
                  options={daysOptions}
                  value={formData?.days}
                  onChange={(value) => handleInputChange('days', value)}
                  error={errors?.days}
                  required
                />

                <Input
                  type="number"
                  label="Budget (PHP)"
                  placeholder="Enter your total budget"
                  value={formData?.budget}
                  onChange={(e) => handleInputChange('budget', e?.target?.value)}
                  error={errors?.budget}
                  description="Total budget in Philippine Pesos for accommodation and meals"
                  required
                />

                {errorMessage && (
                  <div className="bg-destructive/10 border border-destructive rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                    <p className="text-sm text-destructive">{errorMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={loading}
                  iconName="Wand2"
                  className="bg-gradient-to-r from-[#2E8B8B] to-[#F4A261] hover:opacity-90"
                >
                  {loading ? 'Generating Your Trip Plan...' : 'Generate Trip Plan'}
                </Button>
              </form>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="Hotel" size={32} className="text-[#2E8B8B] mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Smart Hotel Selection</h3>
                <p className="text-sm text-muted-foreground">AI-curated accommodations matching your budget</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="UtensilsCrossed" size={32} className="text-[#F4A261] mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Restaurant Recommendations</h3>
                <p className="text-sm text-muted-foreground">Curated dining options for every meal</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="MessageCircle" size={32} className="text-[#2E8B8B] mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">AI Travel Assistant</h3>
                <p className="text-sm text-muted-foreground">Get instant answers to your travel questions</p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {tripPlan && (
          <TripPlanResults 
            tripPlan={tripPlan} 
            onReset={handleReset}
            originalBudget={parseFloat(formData?.budget)}
          />
        )}
      </div>
    </div>
  );
};

export default TripPlanner;